// Frontend API. The Claude-design web is in the middle of swapping from
// fixture-only data to the live MongoDB backend. Each exported function is
// either:
//   - REAL — `fetch()` against `/api/*` (mounted in packages/server), with a
//     graceful fallback to the prototype fixture when the matter/client/etc.
//     doesn't exist in Mongo yet.
//   - MOCK — kept in-memory for v1 because the source endpoint doesn't exist
//     yet (AI stream).
// The "mock.ts" name is preserved so callers don't have to thrash through a
// rename in the same commit; a follow-up can split into api/firm.ts etc.

import { getAuthHeaders } from "./auth";
import { fetchCaseDocuments } from "./documents";
import { daysUntilDay, itemDayKey } from "./firmDeadlines";
import { clientById } from "../data/clients";
import { MATTERS, matterById } from "../data/matters";
import { practiceAreaLabel } from "../data/practice-areas";
import type { Client, Deadline, Matter, PracticeAreaId } from "../data/types";
import type { CaseTeamMember, FirmCountsResponse } from "@duralex/shared";

const ms = (n: number) => new Promise((res) => setTimeout(res, n));

const withClientName = (m: Matter): Matter => ({ ...m, clientName: clientById(m.clientId)?.name });
const allMatters = (): Matter[] => Object.values(MATTERS).map(withClientName);

// ---------------------------------------------------------------------------
// The one map between the server's practice buckets (immigration |
// personalInjury | criminalDefense | other) and the frontend's ids. "other"
// is a real bucket with no entry in the 13-area taxonomy; dropping it would
// make those cases vanish from every count.
// ---------------------------------------------------------------------------
export const SERVER_TO_FRONTEND_PA: Record<string, PracticeAreaId | "other"> = {
  immigration: "immigration",
  personalInjury: "pi",
  criminalDefense: "criminal",
  other: "other",
};

/** The server's practice-area bucket in the frontend's words. */
export function serverPracticeAreaLabel(id: string): string {
  return practiceAreaLabel(SERVER_TO_FRONTEND_PA[id] ?? id);
}

/** The frontend id's server bucket: the /cases?area= a live number reconciles with. */
export function serverPracticeAreaOf(feId: string): string {
  return Object.keys(SERVER_TO_FRONTEND_PA).find((k) => SERVER_TO_FRONTEND_PA[k] === feId) ?? feId;
}

export async function fetchFirmCounts(): Promise<FirmCountsResponse> {
  const res = await fetch("/api/firm/counts", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`fetchFirmCounts: ${res.status}`);
  return (await res.json()) as FirmCountsResponse;
}

export interface ClientWithMatters extends Client {
  matters: Matter[];
  // Real-client extras the prototype Client type does not carry.
  alienNumber?: string;
  status?: string;
  dateOfBirth?: string;
  /**
   * Set when the fixture stood in: "missing" - the id is not in the firm
   * database; "unreachable" - the server could not be reached.
   */
  fixtures?: "missing" | "unreachable";
}

// ---------------------------------------------------------------------------
// Server -> prototype adapters. The server's Case schema (see
// packages/server/src/models/Case.ts) has a much richer shape than the
// prototype's Matter; map the relevant fields and default the rest so the
// existing UI renders without per-component conditionals.
// ---------------------------------------------------------------------------

export interface ServerCaseShape {
  _id: string;
  eId?: string;
  caseNumber?: string;
  practiceArea?: string;
  practiceAreaTemplateId?: string | null;
  matterData?: Record<string, unknown>;
  caseType?: string;
  caseStage?: string;
  status?: string;
  needsReview?: boolean;
  priority?: string;
  clientId?: string | { _id?: string };
  clientData?: {
    personal?: {
      firstName?: string;
      lastName?: string;
      alienNumber?: string;
    };
    contact?: { email?: string; phone?: string; preferredLanguage?: string };
    merged?: {
      fullName?: string;
      // The legacy Drive-sync shape; old rows carry the name nowhere else.
      clientName?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
    };
  };
  immigrationSpecific?: {
    nextHearing?: string;
    nextHearingSource?: string;
    oneYearDeadline?: string;
    courtAddress?: string;
  };
  deadlines?: Array<{
    label: string;
    date: string;
    type: string;
    completed?: boolean;
    /** The server's word on a row at UTC midnight: a day, or a timed hearing. */
    allDay?: boolean;
    /** "critical" on a hard clock - an RFE or NOID response. */
    severity?: string;
  }>;
  missingInfo?: string[];
  aiSummary?: { text?: string; generatedAt?: string };
  syncStatus?: { driveFolderId?: string };
  team?: CaseTeamMember[];
  createdAt?: string;
}

/** The id the matter screens route on. */
export function matterRouteId(matter: Pick<ServerCaseShape, "_id" | "eId" | "caseNumber">): string {
  return matter.eId ?? matter.caseNumber ?? matter._id;
}

// The merged profile first, raw intake second - the same order the server
// resolves the payer in (services/clientResolver.ts), so a form that
// offers to email the client names the address the mail will go to.
export function clientEmailOf(server: ServerCaseShape): string {
  return server.clientData?.merged?.email ?? server.clientData?.contact?.email ?? "";
}

// resolveFullName's candidates in its order (routes/cases.ts), for resolved
// list rows and raw by-eid documents alike; a token equal to "unknown" in any
// case is a placeholder half, as named() in services/clientName.ts reads it.
export function clientNameOf(server: ServerCaseShape): string | undefined {
  const m = server.clientData?.merged;
  const p = server.clientData?.personal;
  const candidates = [
    m?.fullName,
    m?.clientName,
    [m?.firstName, m?.lastName].join(" "),
    [p?.firstName, p?.lastName].join(" "),
  ];
  for (const c of candidates) {
    const v = (c ?? "")
      .split(/\s+/)
      .filter((t) => t && t.toLowerCase() !== "unknown")
      .join(" ");
    if (v) return v;
  }
  return undefined;
}

// The server's one-year engine owns this row and rewrites it on every
// extraction, so the strip offers no close on it.
const ONE_YEAR_LABEL_PREFIX = "Asylum 1-Year Filing Deadline";

export type ServerDeadlineRow = NonNullable<ServerCaseShape["deadlines"]>[number];

// The strip's rows from the server's. A close answers the rows alone, and
// the hero writes them over the cached matter through this same function.
export function adaptDeadlines(serverId: string, rows: ServerDeadlineRow[]): Deadline[] {
  return rows
    .filter((d) => d?.date && d.completed !== true)
    .map((d) => {
      // Negative on purpose: a deadline that has passed is a fact the screens
      // have to say out loud, and clamping it to zero read as "due today".
      // "passed" is its own bucket: a missed row must not answer a question
      // about the days ahead (the server counts a missed obligation as
      // overdue, a past event not at all).
      const days = daysUntilDay(d.date, d.allDay);
      // The server's word on a hard clock is critical from the day it is
      // known; a passed row stays passed.
      const bucket: Deadline["bucket"] =
        days < 0
          ? "passed"
          : days <= 3 || d.severity === "critical"
            ? "critical"
            : days <= 7
              ? "thisWeek"
              : "upcoming";
      return {
        id: `${serverId}:${d.label}:${d.date}`,
        when: itemDayKey(d.date, d.allDay),
        day: d.label?.startsWith(ONE_YEAR_LABEL_PREFIX) ? undefined : d.date.slice(0, 10),
        label: d.label,
        bucket,
        days,
        type: d.type,
      };
    })
    .sort((a, b) => a.days - b.days);
}

export function adaptCaseToMatter(server: ServerCaseShape): Matter {
  const eID = matterRouteId(server);
  const clientIdRaw = server.clientId;
  const clientId =
    typeof clientIdRaw === "string" ? clientIdRaw : (clientIdRaw?._id ?? `mongo:${server._id}`);

  const fePracticeArea: PracticeAreaId | string =
    SERVER_TO_FRONTEND_PA[server.practiceArea ?? ""] ?? server.practiceArea ?? "other";

  const deadlines = adaptDeadlines(server._id, server.deadlines ?? []);

  const imm = server.immigrationSpecific;
  const rows = server.deadlines ?? [];
  // A day unless the calendar gave an instant, read as the agenda reads it
  // (routes/firmDashboard.ts). A stored day already behind us is not next.
  const hearingDay = imm?.nextHearing
    ? itemDayKey(
        imm.nextHearing,
        imm.nextHearingSource && imm.nextHearingSource !== "calendar" ? true : undefined,
      )
    : undefined;
  const nextHearingDate =
    hearingDay && daysUntilDay(hearingDay, true) >= 0 ? hearingDay : undefined;
  const nextHearingKind = nextHearingDate
    ? rows.some((d) => d?.type === "interview" && String(d.date).slice(0, 10) === nextHearingDate)
      ? "interview"
      : "hearing"
    : undefined;
  const oneYearRow = rows.find((d) => d?.label?.startsWith(ONE_YEAR_LABEL_PREFIX));

  const driveFolder = server.syncStatus?.driveFolderId
    ? `https://drive.google.com/drive/folders/${server.syncStatus.driveFolderId}`
    : undefined;

  return {
    eID,
    clientId,
    clientName: clientNameOf(server),
    practiceArea: fePracticeArea,
    practiceAreaTemplateId: server.practiceAreaTemplateId ?? null,
    subtype: server.caseType ?? undefined,
    stage: server.caseStage ?? "intake",
    opened: (server.createdAt ?? new Date().toISOString()).slice(0, 10),
    status: server.status === "closed" || server.status === "archived" ? "closed" : "active",
    serverStatus: server.status,
    needsReview: server.needsReview === true,
    needsAttention: server.priority === "urgent" || (server.missingInfo?.length ?? 0) > 0,
    tags: [],
    driveFolder,
    matter: {
      type: server.caseType ?? "other",
      subtype: server.caseType ?? undefined,
      stage: server.caseStage ?? undefined,
      receiptNumber: null,
      aNumber: server.clientData?.personal?.alienNumber,
      filedDate: undefined,
      venue: server.immigrationSpecific?.courtAddress,
    },
    deadlines,
    nextHearingDate,
    nextHearingKind,
    oneYearDeadline: imm?.oneYearDeadline ? itemDayKey(imm.oneYearDeadline, true) : undefined,
    oneYearFiled: oneYearRow?.completed === true,
    team: server.team ?? [],
    documents: [],
    missing: (server.missingInfo ?? []).map((m, i) => ({
      id: `m-${i}`,
      what: m,
      priority: "soon" as const,
    })),
    aiSummary: server.aiSummary?.text
      ? { text: server.aiSummary.text, generatedAt: server.aiSummary.generatedAt }
      : undefined,
    // Schemaless per-matter bag (server side: Case.matterData). The
    // universal MatterDashboard renderer reads this through `pick<T>()`.
    workspace: server.matterData ?? {},
  };
}

function adaptCaseToClient(server: ServerCaseShape): Client {
  const name = clientNameOf(server) ?? "Unknown";
  const clientIdRaw = server.clientId;
  const id =
    typeof clientIdRaw === "string" ? clientIdRaw : (clientIdRaw?._id ?? `mongo:${server._id}`);
  const eID = matterRouteId(server);
  return {
    id,
    name,
    altSpellings: [],
    primaryLanguage: server.clientData?.contact?.preferredLanguage,
    contactEmail: clientEmailOf(server),
    contactPhone: server.clientData?.contact?.phone ?? "",
    nationality: "",
    matterIds: [eID],
    intakeDate: (server.createdAt ?? new Date().toISOString()).slice(0, 10),
  };
}

// The real Client model fields (packages/server/src/models/Client.ts).
// The previous version of this shape read 8 fields the server never
// sends, so every real client rendered with a blank phone and "en".
interface ServerClientResponse {
  client: {
    _id: string;
    firstName?: string;
    lastName?: string;
    alienNumber?: string;
    dateOfBirth?: string;
    email?: string;
    primaryPhone?: string;
    preferredLanguage?: string;
    status?: string;
    createdAt?: string;
  };
  matters: ServerCaseShape[];
}

function adaptServerClient(payload: ServerClientResponse): ClientWithMatters {
  const c = payload.client;
  const matters = (payload.matters ?? []).map(adaptCaseToMatter);
  return {
    id: c._id,
    name: [c.firstName, c.lastName].filter(Boolean).join(" "),
    // Required by the prototype Client type but absent on the server;
    // left empty, and the card no longer renders them.
    altSpellings: [],
    nationality: "",
    primaryLanguage: c.preferredLanguage,
    contactEmail: c.email ?? "",
    contactPhone: c.primaryPhone ?? "",
    matterIds: matters.map((m) => m.eID),
    intakeDate: (c.createdAt ?? "").slice(0, 10),
    alienNumber: c.alienNumber,
    status: c.status,
    dateOfBirth: c.dateOfBirth ? c.dateOfBirth.slice(0, 10) : undefined,
    matters,
  };
}

/**
 * Looks up a client by Mongo `_id` first; falls back to the in-memory
 * fixture when the id matches a prototype client (fixture ids like
 * "cl-anvar" are not ObjectIds, so the server answers 400/404). The
 * fallback carries a `fixtures` marker so the screen can say so.
 */
export async function fetchClient(clientId: string): Promise<ClientWithMatters | null> {
  let reason: "missing" | "unreachable" = "missing";
  try {
    const res = await fetch(`/api/clients/${encodeURIComponent(clientId)}`, {
      headers: { ...getAuthHeaders() },
      credentials: "same-origin",
    });
    if (res.ok) {
      const payload = (await res.json()) as ServerClientResponse;
      return adaptServerClient(payload);
    }
    // 400 (bad ObjectId) / 404 (no such client, or one the viewer may not
    // see) mean the id is not there for them; any other status (500, 401)
    // says nothing about it.
    if (res.status !== 400 && res.status !== 404) reason = "unreachable";
  } catch {
    reason = "unreachable";
  }
  await ms(15);
  const c = clientById(clientId);
  if (!c) {
    // A real id with the server down is not "no such client" - let the
    // query error so the screen can say the record is unavailable.
    if (reason === "unreachable") throw new Error("GET /api/clients/:id unreachable");
    return null;
  }
  return {
    ...c,
    matters: (c.matterIds || [])
      .map((eID) => matterById(eID))
      .filter((m): m is Matter => Boolean(m))
      .map(withClientName),
    fixtures: reason,
  };
}

export interface MatterWithClient extends Matter {
  client: Client | null;
  /**
   * Mongo `_id` for the underlying Case document, when this matter came
   * from the live backend. Undefined for prototype/fixture matters. Used
   * by the team editor to PUT /api/cases/:id/team, which is keyed on `_id`.
   */
  mongoId?: string;
  /**
   * Set when this matter is a prototype fixture rather than the firm's
   * record. The screen must say so: one click from a fixture list into a
   * full fabricated dossier is the most expensive lie in the product.
   */
  fixtures?: "missing" | "unreachable";
}

export interface MatterListResult {
  matters: Matter[];
  /** What this ask matched within the viewer's scope - larger than `matters` when the page is capped. */
  total: number;
  /**
   * Set when `matters` are prototype fixtures rather than the firm's data.
   * The screen must say so: fabricated matters presented as real is worse
   * than an empty page.
   */
  fixtures?: "unreachable";
}

// One page, and 100 is the server's own ceiling (listQuerySchema in
// routes/cases.ts). A firm with thousands of matters gets the newest 100
// plus the true total, rather than twenty requests to paint one screen.
const MATTER_PAGE = 100;

/** The newest page of the open caseload for the index, or the prototype
 * fixtures when the backend cannot be reached. An empty answer is the
 * viewer's empty scope, never a reason for fixtures. A filtered ask
 * (search, review queue, area) never falls back to fixtures either:
 * prototype rows posing as hits would be invented data, so a failure
 * throws. */
export async function fetchMatterList(
  search?: string,
  opts?: { needsReview?: boolean; area?: string },
): Promise<MatterListResult> {
  const q = search ? `&search=${encodeURIComponent(search)}` : "";
  const review = opts?.needsReview ? "&needsReview=true" : "";
  const area = opts?.area ? `&practiceArea=${encodeURIComponent(opts.area)}` : "";
  // The index shows the open caseload, the counts' own definition. A search
  // reaches closed matters and leads on purpose, and the queue holds leads.
  const scope = search || opts?.needsReview ? "" : "&status=open";
  const filtered = Boolean(search || opts?.needsReview || opts?.area);
  try {
    const res = await fetch(`/api/cases?limit=${MATTER_PAGE}${scope}${q}${review}${area}`, {
      headers: { ...getAuthHeaders() },
      credentials: "same-origin",
    });
    if (res.ok) {
      const json = (await res.json()) as { cases?: ServerCaseShape[]; total?: number };
      const cases = json.cases ?? [];
      return { matters: cases.map(adaptCaseToMatter), total: json.total ?? cases.length };
    }
  } catch {
    /* fall through - the filtered guard below rethrows */
  }
  if (filtered) throw new Error("matter list failed");
  const matters = allMatters();
  return { matters, total: matters.length, fixtures: "unreachable" };
}

/**
 * Looks up a matter by eID against the live backend
 * (`GET /api/cases/by-eid/:eId`) and adapts the rich Case shape into the
 * prototype's Matter shape. Falls back to the in-memory fixture when the
 * eID isn't in Mongo yet (prototype IDs like "DL-YJF4Q5"), so the design
 * preview keeps working alongside real-data matters - carrying a
 * `fixtures` marker so the screen can say so.
 */
export async function fetchMatter(eID: string): Promise<MatterWithClient | null> {
  let reason: "missing" | "unreachable" = "missing";
  try {
    const res = await fetch(`/api/cases/by-eid/${encodeURIComponent(eID)}`, {
      headers: { ...getAuthHeaders() },
      credentials: "same-origin",
    });
    if (res.ok) {
      const server = (await res.json()) as ServerCaseShape;
      const matter = adaptCaseToMatter(server);
      const client = adaptCaseToClient(server);
      // The case payload carries document ids, not documents; the doc wall
      // needs the records themselves.
      const documents = await fetchCaseDocuments(server._id);
      return { ...matter, documents, client, mongoId: server._id };
    }
    // The route answers 404 for an eID it does not hold and nothing else
    // (it validates no id format); any other status is the server, not
    // the matter.
    if (res.status !== 404) reason = "unreachable";
  } catch {
    reason = "unreachable";
  }
  await ms(15);
  const m = matterById(eID);
  if (!m) {
    // A real eID with the server down is not "no such matter" - let the
    // query error so the screen can say the record is unavailable.
    if (reason === "unreachable") throw new Error("GET /api/cases/by-eid/:eId unreachable");
    return null;
  }
  const c = clientById(m.clientId);
  return { ...withClientName(m), client: c, fixtures: reason };
}

// Gmail messages. The server mounts this router at /api/gmail behind the
// same auth gate as the rest of the API; the connection VERDICT comes
// from the integrations aggregate, not from here.
export interface GmailThread {
  from?: string;
  subject?: string;
  ago?: string;
}

/** `"Google <noreply@google.com>"` -> `"Google"`; a bare address stays as is. */
export function senderName(from: string | undefined): string | undefined {
  if (!from) return undefined;
  const named = from.match(/^\s*"?([^"<]+?)"?\s*</);
  if (named) return named[1].trim();
  return from.replace(/[<>]/g, "").trim();
}

/** Compact age for the inbox card: "4m ago", "3h ago", "2d ago". */
export function timeAgo(date: string | undefined, now: number = Date.now()): string | undefined {
  if (!date) return undefined;
  const then = new Date(date).getTime();
  if (Number.isNaN(then)) return undefined;
  const mins = Math.max(0, Math.round((now - then) / 60_000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export async function fetchGmailThreads(
  max = 5,
): Promise<{ threads: GmailThread[]; reason?: string }> {
  try {
    const r = await fetch(`/api/gmail/messages?limit=${max}`, {
      headers: { ...getAuthHeaders() },
      credentials: "same-origin",
    });
    if (!r.ok) return { threads: [], reason: `HTTP ${r.status}` };
    const body = (await r.json()) as {
      messages?: Array<{ from?: string; subject?: string; date?: string }>;
    };
    return {
      threads: (body.messages ?? []).map((m) => ({
        from: senderName(m.from),
        subject: m.subject,
        ago: timeAgo(m.date),
      })),
    };
  } catch {
    return { threads: [], reason: "Server not reachable" };
  }
}

// ---------------------------------------------------------------------------
// Update matter team - PUT /api/cases/:id/team, keyed on the Mongo `_id`.
// The route answers with the rows it stored; a refusal (an admin-only
// route, a folded duplicate) throws with the server's word.
// ---------------------------------------------------------------------------

export interface CaseTeamPayload {
  team: CaseTeamMember[];
}

export async function updateCaseTeam(
  caseId: string,
  payload: CaseTeamPayload,
): Promise<CaseTeamPayload> {
  const res = await fetch(`/api/cases/${encodeURIComponent(caseId)}/team`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error || `Team update failed: ${res.status}`);
  }
  return (await res.json()) as CaseTeamPayload;
}

// ---------------------------------------------------------------------------
// Client expansion. Each helper hits a real endpoint; on 404/4xx/network
// failure it returns an empty list so the UI renders an explicit empty
// state instead of crashing.
// ---------------------------------------------------------------------------

export interface ClientRelative {
  caseId: string;
  caseEid?: string;
  index: number;
  relationship?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string | null;
  alienNumber?: string;
  inUS?: boolean;
  status?: string;
}

export interface ClientJudge {
  _id: string;
  updatedAt?: string;
  name: string;
  title?: string;
  court?: string;
  district?: string;
  contactPhone?: string;
  contactEmail?: string;
  practiceAreas?: string[];
  notes?: string;
}

export interface ClientOpposingCounsel {
  _id: string;
  updatedAt?: string;
  name: string;
  firm?: string;
  email?: string;
  phone?: string;
  role?: string;
  practiceAreas?: string[];
}

async function safeFetchJson<T>(url: string, fallback: T): Promise<T> {
  try {
    const res = await fetch(url, {
      headers: { ...getAuthHeaders() },
      credentials: "same-origin",
    });
    if (!res.ok) return fallback;
    return (await res.json()) as T;
  } catch {
    return fallback;
  }
}

export async function fetchClientRelatives(clientId: string): Promise<ClientRelative[]> {
  const json = await safeFetchJson<{ relatives?: ClientRelative[] }>(
    `/api/clients/${encodeURIComponent(clientId)}/relatives`,
    { relatives: [] },
  );
  return json.relatives ?? [];
}

export async function fetchClientJudges(
  clientId: string,
): Promise<{ judges: ClientJudge[]; unlinkedJudgeNames: string[] }> {
  const json = await safeFetchJson<{ judges?: ClientJudge[]; unlinkedJudgeNames?: string[] }>(
    `/api/clients/${encodeURIComponent(clientId)}/judges`,
    { judges: [], unlinkedJudgeNames: [] },
  );
  return {
    judges: json.judges ?? [],
    unlinkedJudgeNames: json.unlinkedJudgeNames ?? [],
  };
}

export async function fetchClientOpposingCounsel(
  clientId: string,
): Promise<ClientOpposingCounsel[]> {
  const json = await safeFetchJson<{ counsel?: ClientOpposingCounsel[] }>(
    `/api/clients/${encodeURIComponent(clientId)}/opposing-counsel`,
    { counsel: [] },
  );
  return json.counsel ?? [];
}

export async function fetchClientClosedMatters(clientId: string): Promise<Matter[]> {
  const json = await safeFetchJson<{ matters?: ServerCaseShape[] }>(
    `/api/clients/${encodeURIComponent(clientId)}/closed-matters`,
    { matters: [] },
  );
  return (json.matters ?? []).map(adaptCaseToMatter);
}

// ── Cross-matter intelligence signals ────────────────────────────────────
// Server-side (packages/server/src/routes/clients.ts) detects rules-driven
// signals like deadline stacks, conflicts of interest, crimmigration risk,
// settlement-trust implications, and estate updates needed. The endpoint
// already filters out dismissed signals server-side, so the UI only sees
// what's currently visible. 4xx/network failures degrade to an empty list.

export interface ClientIntelSignal {
  id: string;
  type: string;
  severity: "info" | "warning" | "critical";
  title: string;
  explanation: string;
  suggestedAction?: string;
  matters: string[];
}

export async function fetchClientIntelligence(clientId: string): Promise<ClientIntelSignal[]> {
  const json = await safeFetchJson<{ signals?: ClientIntelSignal[] }>(
    `/api/clients/${encodeURIComponent(clientId)}/intelligence`,
    { signals: [] },
  );
  return json.signals ?? [];
}

// ---------------------------------------------------------------------------
// Judge / OpposingCounsel detail pages.
// ---------------------------------------------------------------------------

export interface JudgeDetail {
  judge: ClientJudge | null;
  matters: Matter[];
}

export async function fetchJudgeDetail(judgeId: string): Promise<JudgeDetail> {
  const json = await safeFetchJson<{ judge?: ClientJudge; matters?: ServerCaseShape[] }>(
    `/api/judges/${encodeURIComponent(judgeId)}`,
    { judge: undefined, matters: [] },
  );
  return {
    judge: json.judge ?? null,
    matters: (json.matters ?? []).map(adaptCaseToMatter),
  };
}

export interface OpposingCounselDetail {
  counsel: ClientOpposingCounsel | null;
  matters: Matter[];
}

export async function fetchOpposingCounselDetail(
  counselId: string,
): Promise<OpposingCounselDetail> {
  const json = await safeFetchJson<{
    counsel?: ClientOpposingCounsel;
    matters?: ServerCaseShape[];
  }>(`/api/opposing-counsel/${encodeURIComponent(counselId)}`, {
    counsel: undefined,
    matters: [],
  });
  return {
    counsel: json.counsel ?? null,
    matters: (json.matters ?? []).map(adaptCaseToMatter),
  };
}
